import React, { useState, useEffect } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, ShieldCheckIcon, TrashIcon, PlusIcon } from '@heroicons/react/24/outline';
import { updateTenant, activateTenant, fetchTenantSafeIPs, addTenantSafeIP, removeTenantSafeIP } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import InactiveStateModal from './tenants/InactiveStateModal';
import Badge from './ui/Badge';
import { describeDeletion, isInactiveStatus, lifecycleBadge, logPurgeView, takenPurgeDates } from '../utils/tenantLifecycle';

const LICENSE_TIERS = [
  { value: 'Basic', label: 'Basic' },
  { value: 'Identity', label: 'Identity' },
  { value: 'Defender', label: 'Defender' },
];

// Microsoft cloud the tenant lives in. Blank is equivalent to Commercial. Operator-declared —
// never auto-detected from licensing. Log collection uses it to skip feeds Microsoft doesn't
// support in government clouds (today: message trace, unsupported in GCC).
const CLOUD_ENVIRONMENTS = [
  { value: 'Commercial', label: 'Commercial' },
  { value: 'GCC', label: 'GCC' },
  { value: 'GCCHigh', label: 'GCC High' },
  { value: 'DoD', label: 'DoD' },
];

// Date-only display (UTC) for an ISO 8601 string; '—' when absent/invalid.
const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
};

export default function EditTenantDrawer({ isOpen, tenant, allTenants = [], onClose, onSuccess }) {
  const [friendlyName, setFriendlyName] = useState('');
  const [shortName, setShortName] = useState('');
  const [msp, setMsp] = useState('');
  const [domain, setDomain] = useState('');
  const [licenseTier, setLicenseTier] = useState('');
  const [cloudEnvironment, setCloudEnvironment] = useState('');
  const [signInCollection, setSignInCollection] = useState('FALSE');
  const [auditCollection, setAuditCollection] = useState('FALSE');
  const [azureSubIds, setAzureSubIds] = useState('');
  const [tokenLifetimeMinutes, setTokenLifetimeMinutes] = useState('');

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  // Safe IPs state
  const [safeIPs, setSafeIPs] = useState([]);
  const [safeIPsLoading, setSafeIPsLoading] = useState(false);
  const [newSafeIP, setNewSafeIP] = useState('');
  const [newSafeIPLabel, setNewSafeIPLabel] = useState('');
  const [addingSafeIP, setAddingSafeIP] = useState(false);
  const [safeIPError, setSafeIPError] = useState(null);
  const [confirmRemoveSafeIP, setConfirmRemoveSafeIP] = useState(null);
  const [removingSafeIP, setRemovingSafeIP] = useState(false);

  // Inactive lifecycle (2026-09-29): one modal for deactivate / change state / schedule deletion.
  // `lifecycleModal` is null, or the reason the modal opens preset to ('' = keep the current one).
  const { user } = useAuth();
  const isGlobalAdmin = !!user?.isAdmin; // isAdmin is GLOBAL admin only
  const [lifecycleModal, setLifecycleModal] = useState(null);
  const [reactivating, setReactivating] = useState(false);

  // Populate form when tenant changes
  useEffect(() => {
    if (tenant) {
      setFriendlyName(tenant.friendlyName || '');
      setShortName(tenant.shortName || '');
      setMsp(tenant.msp || '');
      setDomain(tenant.domain || '');
      setLicenseTier(tenant.licenseTier || '');
      setCloudEnvironment(tenant.cloudEnvironment || '');
      setSignInCollection(tenant.m365SignInLogCollection || 'FALSE');
      setAuditCollection(tenant.m365AuditLogCollection || 'FALSE');
      setAzureSubIds(tenant.azureSubIds || '');
      setTokenLifetimeMinutes(tenant.tokenLifetimeMinutes || '');
      setSaveError(null);
      setSafeIPError(null);
      setNewSafeIP('');
      setNewSafeIPLabel('');
      setConfirmRemoveSafeIP(null);

      // Load tenant safe IPs
      setSafeIPsLoading(true);
      fetchTenantSafeIPs(tenant.tenantId)
        .then((ips) => setSafeIPs(Array.isArray(ips) ? ips : []))
        .catch((err) => setSafeIPError(err.message))
        .finally(() => setSafeIPsLoading(false));
    }
  }, [tenant]);

  const handleAddSafeIP = async () => {
    const ip = newSafeIP.trim();
    if (!ip) return;
    setAddingSafeIP(true);
    setSafeIPError(null);
    try {
      const created = await addTenantSafeIP(tenant.tenantId, { ipAddress: ip, label: newSafeIPLabel.trim() });
      setSafeIPs((prev) => [created, ...prev]);
      setNewSafeIP('');
      setNewSafeIPLabel('');
    } catch (err) {
      setSafeIPError(err.message);
    } finally {
      setAddingSafeIP(false);
    }
  };

  const handleRemoveSafeIP = async (ipAddress) => {
    setRemovingSafeIP(true);
    setSafeIPError(null);
    try {
      await removeTenantSafeIP(tenant.tenantId, ipAddress);
      setSafeIPs((prev) => prev.filter((ip) => ip.ipAddress !== ipAddress));
      setConfirmRemoveSafeIP(null);
    } catch (err) {
      setSafeIPError(err.message);
    } finally {
      setRemovingSafeIP(false);
    }
  };

  // Reactivation = Activate: it starts the Activity Feed subscriptions, turns collection back on and clears
  // the lifecycle columns (including any scheduled purge).
  const handleReactivate = async () => {
    const confirmed = window.confirm(
      `Reactivate "${tenant.friendlyName || tenant.tenantId}"?\n\nLog collection restarts and any scheduled deletion is cancelled.`
    );
    if (!confirmed) return;
    setReactivating(true);
    setSaveError(null);
    try {
      await activateTenant(tenant.tenantId);
      onSuccess();
      onClose();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setReactivating(false);
    }
  };

  const handleLifecycleDone = () => {
    setLifecycleModal(null);
    onSuccess();
    onClose();
  };

  const handleClose = () => {
    setSaveError(null);
    setSaving(false);
    onClose();
  };

  const handleSave = async () => {
    if (!friendlyName.trim()) {
      setSaveError('Friendly name is required.');
      return;
    }
    if (!domain.trim()) {
      setSaveError('Domain is required.');
      return;
    }
    if (tokenLifetimeMinutes !== '' && (isNaN(Number(tokenLifetimeMinutes)) || Number(tokenLifetimeMinutes) < 15 || Number(tokenLifetimeMinutes) > 1440)) {
      setSaveError('Token lifetime must be a number between 15 and 1440 minutes.');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await updateTenant(tenant.tenantId, {
        friendlyName: friendlyName.trim(),
        shortName: shortName.trim(),
        msp: msp.trim(),
        domain: domain.trim(),
        licenseTier,
        cloudEnvironment,
        m365SignInLogCollection: signInCollection,
        m365AuditLogCollection: auditCollection,
        azureSubIds: azureSubIds.trim(),
        tokenLifetimeMinutes: tokenLifetimeMinutes.toString().trim(),
      });
      onSuccess();
      handleClose();
    } catch (err) {
      setSaveError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Fields are read-only on a legacy 'deleted' row and once the purge has removed the portal records.
  const isDeleted = tenant?.status === 'deleted' || !!tenant?.portalPurgedUtc;
  const isInactive = isInactiveStatus(tenant?.status);
  const busy = saving || reactivating;

  if (!isOpen || !tenant) return null;
  const badge = lifecycleBadge(tenant);
  const logPurge = logPurgeView(tenant);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-40"
        onClick={handleClose}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-[480px] bg-gray-900 border-l border-gray-800 shadow-2xl z-50 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h2 className="text-lg font-semibold text-gray-100">Edit Tenant</h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Read-only fields */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Tenant ID</label>
            <p className="text-sm text-gray-400 font-mono bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">
              {tenant.tenantId}
            </p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Auth Method</label>
            <p className="text-sm text-gray-400 bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50">
              {tenant.authMethod === 'FederatedIdentity' ? 'Federated Identity Credential (FIC)' : tenant.authMethod || '—'}
            </p>
          </div>

          {/* Monitoring lifecycle dates (read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Monitoring</label>
            <div className="text-sm bg-gray-800/50 px-3 py-2 rounded border border-gray-700/50 divide-y divide-gray-700/40">
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">Onboarded</span>
                <span className="text-gray-300">{fmtDate(tenant.onboardedUtc)}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">Activated</span>
                <span className="text-gray-300">{fmtDate(tenant.activatedUtc)}</span>
              </div>
              <div className="flex items-center justify-between py-1">
                <span className="text-gray-500">First data seen <span className="text-gray-600">(approx.)</span></span>
                <span className="text-gray-300">{fmtDate(tenant.firstEventUtc)}</span>
              </div>
              {isInactive && (
                <>
                  <div className="flex items-center justify-between py-1">
                    <span className="text-gray-500">State</span>
                    <span>{badge ? <Badge tone={badge.tone}>{badge.label}</Badge> : '—'}</span>
                  </div>
                  <div className="flex items-center justify-between py-1">
                    <span className="text-gray-500">Deactivated</span>
                    <span className="text-gray-300">{fmtDate(tenant.deactivatedUtc || tenant.deletedDate)}</span>
                  </div>
                  {tenant.purgeDate && (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-gray-500">Deletion date</span>
                      <span className="text-gray-300">{fmtDate(tenant.purgeDate)}</span>
                    </div>
                  )}
                  {tenant.portalPurgedUtc && (
                    <div className="flex items-center justify-between py-1">
                      <span className="text-gray-500">Portal records deleted</span>
                      <span className="text-gray-300">{fmtDate(tenant.portalPurgedUtc)}</span>
                    </div>
                  )}
                </>
              )}
            </div>
            <p className="text-xs text-gray-600 mt-1">
              Times in UTC. "First data seen" is the earliest log event on record — approximate for tenants
              onboarded before portal tracking.
            </p>
          </div>

          {isInactive && tenant.inactiveReason === 'pending_deletion' && (
            <div className="flex items-start gap-2 p-3 bg-red-900/20 border border-red-700/30 rounded-lg">
              <TrashIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-red-400">
                  {tenant.portalPurgedUtc ? 'Portal records deleted; awaiting log deletion' : 'Scheduled for deletion'}
                </p>
                <p className="text-xs text-red-400/70 mt-0.5">
                  {tenant.portalPurgedUtc
                    ? 'The tenant stays listed until every data team confirms its log data has been deleted. Cost history and operational records are kept.'
                    : <>{describeDeletion(tenant.purgeLogs)} Use “Change state” to postpone or cancel.</>}
                </p>
                {logPurge && (
                  <div className="mt-2">
                    <p className="text-xs font-medium text-gray-400 mb-1">Log deletion</p>
                    {!logPurge.available ? (
                      <p className="text-xs text-amber-400/80">
                        Log deletion status could not be read. This does not mean it is done.
                      </p>
                    ) : (
                      <ul className="space-y-1">
                        {logPurge.teams.map((t) => (
                          <li key={t.team} className="flex items-start justify-between gap-2 text-xs">
                            <span className="text-gray-400">
                              {t.label} <span className="text-gray-600">· {t.covers}</span>
                              {t.detail && <span className="block text-gray-500">{t.detail}</span>}
                            </span>
                            <span className="flex flex-col items-end flex-shrink-0">
                              <Badge tone={t.tone}>{t.statusLabel}</Badge>
                              {t.completedUtc && <span className="text-gray-600 mt-0.5">{fmtDate(t.completedUtc)}</span>}
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <hr className="border-gray-800" />

          {/* Editable fields */}
          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Friendly Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={friendlyName}
              onChange={(e) => setFriendlyName(e.target.value)}
              className="input-field"
              placeholder="e.g. Cohen & Company"
              disabled={isDeleted}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Short Name</label>
            <input
              type="text"
              value={shortName}
              onChange={(e) => setShortName(e.target.value)}
              className="input-field"
              placeholder="e.g. COHEN"
              disabled={isDeleted}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">MSP</label>
            <input
              type="text"
              value={msp}
              onChange={(e) => setMsp(e.target.value)}
              className="input-field"
              placeholder="e.g. PFIT"
              disabled={isDeleted}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Domain <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="input-field"
              placeholder="e.g. cohenusa.com"
              disabled={isDeleted}
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              License Tier
            </label>
            <select
              value={licenseTier}
              onChange={(e) => setLicenseTier(e.target.value)}
              className="input-field"
              disabled={isDeleted}
            >
              <option value="">Select tier...</option>
              {LICENSE_TIERS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">
              Cloud Environment
            </label>
            <select
              value={cloudEnvironment}
              onChange={(e) => setCloudEnvironment(e.target.value)}
              className="input-field"
              disabled={isDeleted}
            >
              <option value="">Commercial (default)</option>
              {CLOUD_ENVIRONMENTS.map((e) => (
                <option key={e.value} value={e.value}>{e.label}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              Only change this for US government cloud tenants. Some feeds are unavailable in
              government clouds — setting this correctly stops false collection alerts.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Sign-In Log Collection</label>
              <select
                value={signInCollection}
                onChange={(e) => setSignInCollection(e.target.value)}
                className="input-field"
                disabled={isDeleted || isInactive}
              >
                <option value="TRUE">Enabled</option>
                <option value="FALSE">Disabled</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">Audit Log Collection</label>
              <select
                value={auditCollection}
                onChange={(e) => setAuditCollection(e.target.value)}
                className="input-field"
                disabled={isDeleted || isInactive}
              >
                <option value="TRUE">Enabled</option>
                <option value="FALSE">Disabled</option>
              </select>
            </div>
          </div>
          {isInactive && (
            <p className="-mt-3 text-xs text-gray-500">
              Collection stays off while the tenant is inactive. Use Reactivate to turn it back on.
            </p>
          )}

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Azure Subscription IDs</label>
            <input
              type="text"
              value={azureSubIds}
              onChange={(e) => setAzureSubIds(e.target.value)}
              className="input-field"
              placeholder="e.g. sub-id-1;sub-id-2"
              disabled={isDeleted}
            />
            <p className="text-xs text-gray-600 mt-1">Semicolon-separated subscription IDs.</p>
          </div>

          <div>
            <label className="block text-xs font-medium text-gray-400 mb-1">Token Lifetime (minutes)</label>
            <input
              type="number"
              value={tokenLifetimeMinutes}
              onChange={(e) => setTokenLifetimeMinutes(e.target.value)}
              className="input-field"
              placeholder="90"
              min={15}
              max={1440}
              disabled={isDeleted}
            />
            <p className="text-xs text-gray-600 mt-1">
              Default: 90. Adjust if tenant has a custom Conditional Access token lifetime policy.
            </p>
          </div>

          <hr className="border-gray-800" />

          {/* Safe IP Addresses Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <ShieldCheckIcon className="h-4 w-4 text-blue-400" />
              <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider">Safe IP Addresses</label>
            </div>
            <p className="text-xs text-gray-600 mb-3">
              IPs flagged here are auto-applied as "Safe" across all investigations for this tenant.
            </p>

            {/* Add form */}
            <div className="flex items-end gap-2 mb-3">
              <div className="flex-1">
                <input
                  type="text"
                  value={newSafeIP}
                  onChange={(e) => setNewSafeIP(e.target.value)}
                  className="input-field font-mono text-xs"
                  placeholder="IP address"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSafeIP()}
                />
              </div>
              <div className="flex-1">
                <input
                  type="text"
                  value={newSafeIPLabel}
                  onChange={(e) => setNewSafeIPLabel(e.target.value)}
                  className="input-field text-xs"
                  placeholder="Label (optional)"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSafeIP()}
                />
              </div>
              <button
                onClick={handleAddSafeIP}
                disabled={addingSafeIP || !newSafeIP.trim()}
                className="btn-primary px-3 py-2 text-xs flex items-center gap-1 flex-shrink-0"
              >
                <PlusIcon className="h-3.5 w-3.5" />
                Add
              </button>
            </div>

            {safeIPError && (
              <div className="flex items-start gap-2 p-2 mb-3 bg-red-900/30 border border-red-700/50 rounded-lg">
                <ExclamationTriangleIcon className="h-3.5 w-3.5 text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-red-400">{safeIPError}</p>
              </div>
            )}

            {/* Safe IP list */}
            {safeIPsLoading ? (
              <div className="text-xs text-gray-600 text-center py-4">Loading safe IPs...</div>
            ) : safeIPs.length === 0 ? (
              <div className="text-xs text-gray-600 text-center py-4">No safe IPs configured for this tenant.</div>
            ) : (
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {safeIPs.map((ip) => (
                  <div
                    key={ip.ipAddress}
                    className="flex items-center justify-between px-3 py-2 bg-gray-800/50 border border-gray-700/50 rounded-lg"
                  >
                    <div className="min-w-0 flex-1">
                      <span className="text-xs font-mono text-gray-200">{ip.ipAddress}</span>
                      {ip.label && <span className="text-xs text-gray-500 ml-2">{ip.label}</span>}
                    </div>
                    {confirmRemoveSafeIP === ip.ipAddress ? (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                          onClick={() => handleRemoveSafeIP(ip.ipAddress)}
                          disabled={removingSafeIP}
                          className="text-xs text-red-400 hover:text-red-300"
                        >
                          {removingSafeIP ? '...' : 'Confirm'}
                        </button>
                        <button
                          onClick={() => setConfirmRemoveSafeIP(null)}
                          className="text-xs text-gray-500 hover:text-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmRemoveSafeIP(ip.ipAddress)}
                        className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0"
                        title="Remove safe IP"
                      >
                        <TrashIcon className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-800 space-y-3">
          {saveError && (
            <div className="flex items-start gap-2 p-3 bg-red-900/30 border border-red-700/50 rounded-lg">
              <ExclamationTriangleIcon className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-red-400">{saveError}</p>
            </div>
          )}

          {/* Lifecycle actions (2026-09-29). Once the portal records are purged there is nothing to change. */}
          {!tenant.portalPurgedUtc && (
            <div className="flex items-center gap-3">
              {isInactive ? (
                <>
                  <button
                    onClick={handleReactivate}
                    disabled={busy}
                    className="flex-1 text-xs px-3 py-2 rounded-lg font-medium bg-green-900/30 text-green-400 border border-green-700/30 hover:bg-green-900/50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {reactivating && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" />}
                    Reactivate Tenant
                  </button>
                  <button
                    onClick={() => setLifecycleModal('')}
                    disabled={busy}
                    className="flex-1 text-xs px-3 py-2 rounded-lg font-medium bg-amber-900/30 text-amber-400 border border-amber-700/30 hover:bg-amber-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Change State…
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setLifecycleModal('retained')}
                    disabled={busy}
                    className="flex-1 text-xs px-3 py-2 rounded-lg font-medium bg-amber-900/30 text-amber-400 border border-amber-700/30 hover:bg-amber-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Deactivate Tenant…
                  </button>
                  <button
                    onClick={() => setLifecycleModal('pending_deletion')}
                    disabled={busy || !isGlobalAdmin}
                    title={isGlobalAdmin ? undefined : 'Scheduling a deletion requires global admin.'}
                    className="flex-1 text-xs px-3 py-2 rounded-lg font-medium bg-red-900/30 text-red-400 border border-red-700/30 hover:bg-red-900/50 transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="h-3.5 w-3.5" />
                    Schedule Deletion…
                  </button>
                </>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={handleClose}
              className="btn-secondary flex-1"
              disabled={busy}
            >
              {isDeleted ? 'Close' : 'Cancel'}
            </button>
            {!isDeleted && (
              <button
                onClick={handleSave}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                disabled={busy}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {lifecycleModal !== null && (
        <InactiveStateModal
          tenant={tenant}
          initialReason={lifecycleModal || undefined}
          canDelete={isGlobalAdmin}
          takenDates={takenPurgeDates(allTenants, tenant.tenantId)}
          onClose={() => setLifecycleModal(null)}
          onDone={handleLifecycleDone}
        />
      )}
    </>
  );
}
